import { useEffect, useMemo, useState } from "react";
import {Box, Container, Paper, Stack, Typography, Grid} from "@mui/material";
import { Trans } from "@lingui/react/macro";
import { PieChart } from '@mui/x-charts/PieChart';
import { useDispatch } from 'react-redux';
import { useGetCuttingAdvancedPresentationQuery } from "../../../apis/Cutting.api";
import {CuttingStateDisplay} from "../../../types/models/CuttingState.d";
import { logApi } from "../../../apis/Log.api";
import { cordwoodApi } from "../../../apis/Cordwood.api";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";
import {stringToColor} from "../../../../utils/color.ts";
import {alpha} from "@mui/material/styles";
import TimelineRootReport from "../../generic/report/TimelineRootReport.tsx";
import * as React from "react";

const ActiveCuttings = () => {
    const dispatch = useDispatch();

    // 1) Load active cuttings (In progress)
    const { data: cuttingData, isLoading: isCuttingsLoading, isFetching: isCuttingsFetching } =
        useGetCuttingAdvancedPresentationQuery({
            list: { page: 0, pageSize: 100 },
            filter: { state: [CuttingStateDisplay.InProgress] as any, orderBys: [] as any[] },
        } as any);

    const activeCuttings = cuttingData?.items || [];
    const activeCuttingIds = activeCuttings.map(c => c.id);

    // 2) Aggregate totals per cutting for two pies: Logs and Cordwood, with shipped/not-shipped outer ring
    const [logsAgg, setLogsAgg] = useState<Record<string, { name: string; total: number; shipped: number; notShipped: number }>>({});
    const [cordAgg, setCordAgg] = useState<Record<string, { name: string; total: number; shipped: number; notShipped: number }>>({});

    useEffect(() => {
        let mounted = true;
        const run = async () => {
            const logsTotals: Record<string, { name: string; total: number; shipped: number; notShipped: number }> = {};
            const cordTotals: Record<string, { name: string; total: number; shipped: number; notShipped: number }> = {};
            for (const c of activeCuttings) {
                const cid = c.id?.toString?.() || String(c.id);
                const name = c.name || cid;
                const [logsSh, logsNs, cordsSh, cordsNs] = await Promise.all([
                    dispatch(logApi.endpoints.getGroup.initiate({ cuttingId: [c.id], shipmentDefined: true } as any)).unwrap().catch(() => []),
                    dispatch(logApi.endpoints.getGroup.initiate({ cuttingId: [c.id], shipmentDefined: false } as any)).unwrap().catch(() => []),
                    dispatch(cordwoodApi.endpoints.getGroup.initiate({ cuttingId: [c.id], shipmentDefined: true } as any)).unwrap().catch(() => []),
                    dispatch(cordwoodApi.endpoints.getGroup.initiate({ cuttingId: [c.id], shipmentDefined: false } as any)).unwrap().catch(() => []),
                ]);
                let lSh = 0, lNs = 0, cSh = 0, cNs = 0;
                for (const r of logsSh as any[]) { lSh += r.totalVolume || 0; }
                for (const r of logsNs as any[]) { lNs += r.totalVolume || 0; }
                for (const r of cordsSh as any[]) { cSh += r.totalVolume || 0; }
                for (const r of cordsNs as any[]) { cNs += r.totalVolume || 0; }
                logsTotals[cid] = { name, shipped: lSh, notShipped: lNs, total: lSh + lNs };
                cordTotals[cid] = { name, shipped: cSh, notShipped: cNs, total: cSh + cNs };
            }
            if (mounted) { setLogsAgg(logsTotals); setCordAgg(cordTotals); }
        };
        if (activeCuttings.length) run(); else { setLogsAgg({}); setCordAgg({}); }
        return () => { mounted = false; };
    }, [dispatch, activeCuttings.map(c => c.id).join(',')]);

    const logsPieInner = useMemo(() => {
        const entries = Object.entries(logsAgg || {}).map(([id, v]) => ({
            id: `l-${id}`,
            label: v.name,
            value: v.total,
            color: stringToColor(v.name),
        }));
        return entries.filter(e => Number.isFinite(e.value) && e.value > 0);
    }, [logsAgg]);
    const logsPieOuter = useMemo(() => {
        const entries = Object.entries(logsAgg || {}).flatMap(([id, v]) => {
            const baseColor = stringToColor(v.name);
            return [
                {
                    id: `l-${id}-sh`,
                    label: `${v.name} • Shipped`,
                    value: v.shipped,
                    color: baseColor,
                },
                {
                    id: `l-${id}-ns`,
                    label: `${v.name} • Not shipped`,
                    value: v.notShipped,
                    color: alpha(baseColor, 0.5),
                },
            ]
        });
        return entries.filter(e => Number.isFinite(e.value) && e.value > 0);
    }, [logsAgg]);

    const cordPieInner = useMemo(() => {
        const entries = Object.entries(cordAgg || {}).map(([id, v]) => ({
            id: `c-${id}`,
            label: v.name,
            value: v.total,
            color: stringToColor(v.name),
        }));
        return entries.filter(e => Number.isFinite(e.value) && e.value > 0);
    }, [cordAgg]);
    const cordPieOuter = useMemo(() => {
        const entries = Object.entries(cordAgg || {}).flatMap(([id, v]) => {
            const baseColor = stringToColor(v.name);
            return [
                {
                    id: `c-${id}-sh`,
                    label: `${v.name} • Shipped`,
                    value: v.shipped,
                    color: baseColor,
                },
                {
                    id: `c-${id}-ns`,
                    label: `${v.name} • Not shipped`,
                    value: v.notShipped,
                    color: alpha(baseColor, 0.5),
                },
            ]
        });
        return entries.filter(e => Number.isFinite(e.value) && e.value > 0);
    }, [cordAgg]);

    const loading = isCuttingsLoading || isCuttingsFetching;

    return (
        <Container maxWidth="lg">
            <Stack spacing={2} sx={{ py: 2 }}>
                <TimelineRootReport
                    filter={{ cuttingId: activeCuttingIds }}
                    showForm={false}
                />
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}><Trans>Active cuttings</Trans></Typography>
                    {(loading) && (
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4}}>
                            <GradientCircularProgress/>
                        </Box>
                    )}
                    {!loading && (
                        (logsPieInner.length || cordPieInner.length) ? (
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6} sx={{ width: '100%', alignItems: 'center' }}>
                                    <Typography variant="subtitle1"><Trans>Logs</Trans></Typography>
                                    {logsPieInner.length ? (
                                        <PieChart
                                            height={420}
                                            width={420}
                                            series={[
                                                { innerRadius: 30, outerRadius: 147, paddingAngle: 0, cornerRadius: 6, data: logsPieInner, arcLabel: (item) => `${item.label}` },
                                                { innerRadius: 150, outerRadius: 200, paddingAngle: 0, cornerRadius: 6, data: logsPieOuter, arcLabel: () => '' },
                                            ]}
                                            slotProps={{ legend: { hidden: false } }}
                                        />
                                    ) : (
                                        <Typography variant="body2"><Trans>No logs data</Trans></Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6} sx={{ width: '100%', alignItems: 'center' }}>
                                    <Typography variant="subtitle1"><Trans>Cordwood</Trans></Typography>
                                    {cordPieInner.length ? (
                                        <PieChart
                                            height={420}
                                            width={420}
                                            series={[
                                                { innerRadius: 30, outerRadius: 147, paddingAngle: 0, cornerRadius: 6, data: cordPieInner, arcLabel: (item) => `${item.label}` },
                                                { innerRadius: 150, outerRadius: 200, paddingAngle: 0, cornerRadius: 6, data: cordPieOuter, arcLabel: () => '' },
                                            ]}
                                            slotProps={{ legend: { hidden: false } }}
                                        />
                                    ) : (
                                        <Typography variant="body2"><Trans>No cordwood data</Trans></Typography>
                                    )}
                                </Grid>
                            </Grid>
                        ) : (
                            <Typography variant="body2"><Trans>No data</Trans></Typography>
                        )
                    )}
                </Paper>
            </Stack>
        </Container>
    )
}

export default ActiveCuttings;
